import { useCohortsFilter } from 'features/Cohorts/hooks/useCohortsFilter'
import { useCaregiverStatusFilter } from 'features/Subscription/hooks/useCaregiverStatusFilter'
import { useOrgStateUnitFilter } from 'features/UI'
import { SyntheticEvent, useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { routes } from 'routes/routes'
import { EOp, FilterModel, UserOrganizationalRoles, UserType, UserTypeTab } from 'types'
import { getRoute } from 'utils'
import { useAdminStatusFilter } from './useAdminStatusFilter'
import { useOrgRoleFilter } from './useOrgRoleFilter'
import { useTraineeStatusFilter } from './useTraineeStatusFilter'
import { useUserStatusFilter } from './useUserStatusFilter'

export const useUsersPageFilter = () => {
  const navigate = useNavigate()
  const { userType } = useParams()

  const selectedTab = useMemo(() => {
    if (!userType) return UserTypeTab.TRAINEE

    return userType as UserTypeTab
  }, [userType])

  const handleChangeSelectedTab = useCallback(
    (_: SyntheticEvent, newValue: UserTypeTab) => {
      const route = getRoute(routes.users, { userType: newValue })
      navigate(route)
    },
    [navigate],
  )

  const { cohortsFilterValue, CohortsFilter } = useCohortsFilter()
  const { cohortsFilterValue: orgUserTagsFilterValue, CohortsFilter: OrgUserTagsFilter } =
    useCohortsFilter()
  const { RoleFilter, rolesFilterValue } = useOrgRoleFilter(selectedTab, 'type')
  const { StatusFilter, statusFilterValue } = useAdminStatusFilter()
  const { UserStatusFilter, userStatusFilterValue } = useUserStatusFilter()
  const { TraineeStatusFilter, traineeStatusFilterValue } = useTraineeStatusFilter()
  const { OrgStateUnitFilter, orgStateUnitFilterValue } = useOrgStateUnitFilter()
  const { CaregiverStatusFilter, caregiverStatusFilterValue } = useCaregiverStatusFilter()

  const filter: FilterModel = useMemo(() => {
    let baseFilter: FilterModel = {
      type: { type: 'text', op: EOp.overlap, value: [selectedTab] },
    }

    switch (selectedTab) {
      case UserTypeTab.ADMIN:
        if (rolesFilterValue) {
          delete baseFilter.type
          baseFilter = {
            ...baseFilter,
            ...rolesFilterValue,
          }
        } else {
          baseFilter.type = {
            type: 'text',
            op: EOp.overlap,
            value: [UserType.ANALYST, UserType.ADMIN],
          }
        }

        if (statusFilterValue) {
          baseFilter.licenseExpiredAt = statusFilterValue
        }
        break
      case UserTypeTab.TRAINEE:
        if (orgStateUnitFilterValue) {
          baseFilter.accessCodeId = orgStateUnitFilterValue
        }
        if (cohortsFilterValue) {
          baseFilter.cohortId = cohortsFilterValue
        }
        if (traineeStatusFilterValue) {
          baseFilter.userStatus = traineeStatusFilterValue
        }
        break
      case UserTypeTab.CAREGIVER:
        if (cohortsFilterValue) {
          baseFilter.cohortId = cohortsFilterValue
        }
        if (caregiverStatusFilterValue) {
          baseFilter = { ...baseFilter, ...caregiverStatusFilterValue }
        }
        break
      case UserTypeTab.ORG:
        if (rolesFilterValue) {
          delete baseFilter.type
          baseFilter = {
            ...baseFilter,
            ...rolesFilterValue,
          }
        } else {
          delete baseFilter.type
          baseFilter.or = [
            {
              type: {
                type: 'text',
                op: EOp.overlap,
                value: [...UserOrganizationalRoles],
              },
            },
            {
              type: {
                type: 'text',
                op: EOp.equals,
                value: [],
              },
            },
          ]
        }

        if (orgStateUnitFilterValue) {
          baseFilter.ownedAccessCodes = orgStateUnitFilterValue
        }
        if (userStatusFilterValue) {
          baseFilter.userStatus = userStatusFilterValue
        }
        if (orgUserTagsFilterValue) {
          baseFilter.cohortId = orgUserTagsFilterValue
        }
        break
    }
    return baseFilter
  }, [
    caregiverStatusFilterValue,
    cohortsFilterValue,
    orgUserTagsFilterValue,
    rolesFilterValue,
    orgStateUnitFilterValue,
    selectedTab,
    statusFilterValue,
    userStatusFilterValue,
    traineeStatusFilterValue,
  ])

  return {
    filter,
    CaregiverStatusFilter,
    CohortsFilter,
    OrgUserTagsFilter,
    RoleFilter,
    StatusFilter,
    OrgStateUnitFilter,
    UserStatusFilter,
    TraineeStatusFilter,
    selectedTab,
    handleChangeSelectedTab,
  }
}
